import { Box, Switch, Typography, Button, Chip, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { useMarketplaces } from "../../../marketplace/hooks/useMarketplaces";
import { useFetchMarketplaces } from "../../../marketplace/hooks/useFetchMarketplaces";
import { IMarketplace } from "../../../marketplace/interfaces/IMarketplace";
import { TipoMarketplaceTitulo, TipoMarketplaceCor } from "../../../marketplace/enum/TipoMarketplace";
import { CircularProgress } from "@mui/material";
import { InfoOutlined, Lock, PlayArrow, Refresh, Stop, WarningAmberOutlined } from "@mui/icons-material";
import { useIFood } from "../../../marketplace/hooks/useIFood";
import { IStatusEstabelecimentoIFood } from "../../../marketplace/interfaces/IStatusEstabelecimentoIFood";
import { CabecalhoCartao, Cartao, PilulaStatus, TituloCartao } from "../../../core/styles/superficies";

interface MarketplaceSwitchesProps {
    caixaAberto: boolean;
}

export const MarketplaceSwitches = ({ caixaAberto }: MarketplaceSwitchesProps) => {
    const theme = useTheme();
    const { marketplaces, loading, handleRefresh } = useFetchMarketplaces();
    const { salvar, loading: loadingSalvar } = useMarketplaces();
    const { consultarStatus } = useIFood();
    const [marketplaceStates, setMarketplaceStates] = useState<Record<string, boolean>>({});
    const [ifoodStatuses, setIfoodStatuses] = useState<Record<string, IStatusEstabelecimentoIFood[]>>({});

    useEffect(() => {
        if (marketplaces) {
            const states: Record<string, boolean> = {};
            marketplaces.forEach(marketplace => {
                states[marketplace.uuid!] = marketplace.configuracao?.status?.aberto ?? false;
            });
            setMarketplaceStates(states);
        }
    }, [marketplaces]);

    // Busca o status real de cada marketplace iFood via API iFood
    useEffect(() => {
        if (!marketplaces) return;
        const ifoodMarketplaces = marketplaces.filter(m => m.tipo === 'IFOOD');
        if (ifoodMarketplaces.length === 0) return;

        const fetchIfoodStatuses = async () => {
            const statusMap: Record<string, IStatusEstabelecimentoIFood[]> = {};
            for (const mp of ifoodMarketplaces) {
                if (!mp.uuid) continue;
                const status = await consultarStatus(mp.uuid);
                if (status && status.length > 0) {
                    statusMap[mp.uuid] = status;
                }
            }
            setIfoodStatuses(statusMap);
        };

        fetchIfoodStatuses();
    }, [marketplaces]);

    const marketplacesControlaveis = (marketplaces ?? []).filter(m =>
        !['PDV', 'IFOOD', 'WHATSAPP'].includes(m.tipo)
    );

    const marketplacesIfood = (marketplaces ?? []).filter(m => m.tipo === 'IFOOD');

    const handleMarketplaceToggle = async (marketplace: IMarketplace, isOpen: boolean) => {
        if (loadingSalvar) return;

        try {
            await salvar({
                ...marketplace,
                configuracao: {
                    ...marketplace.configuracao,
                    status: {
                        aberto: isOpen,
                        mensagem: marketplace.configuracao?.status?.mensagem,
                        dataHoraUltimaAtualizacao: new Date()
                    }
                }
            });

            setMarketplaceStates(prev => ({
                ...prev,
                [marketplace.uuid!]: isOpen
            }));

            handleRefresh();
        } catch (error) {
            console.error('Erro ao alterar status do marketplace:', error);
        }
    };

    const handleToggleAll = async (openAll: boolean) => {
        if (!marketplacesControlaveis.length || loadingSalvar) return;

        const promises = marketplacesControlaveis.map(marketplace =>
            handleMarketplaceToggle(marketplace, openAll)
        );

        await Promise.all(promises);
    };

    if (loading) {
        return (
            <Cartao sx={{ height: 200, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Cartao>
        );
    }

    if (!marketplaces || marketplaces.length === 0) {
        return null;
    }

    const marketplacesExibir = [...marketplacesControlaveis, ...marketplacesIfood];

    if (marketplacesExibir.length === 0) {
        return null;
    }

    const todosAbertos = marketplacesControlaveis.every(m => marketplaceStates[m.uuid!]);
    const todosFechados = marketplacesControlaveis.every(m => !marketplaceStates[m.uuid!]);

    return (
        <Cartao>
            <CabecalhoCartao>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <TituloCartao>Controle de marketplaces</TituloCartao>
                    {marketplacesControlaveis.length > 0 && (
                        <Chip
                            label={`${marketplacesControlaveis.filter(m => marketplaceStates[m.uuid!]).length}/${marketplacesControlaveis.length} abertos`}
                            color={todosAbertos ? "success" : todosFechados ? "error" : "warning"}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.6875rem", fontWeight: 600 }}
                        />
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    {marketplacesControlaveis.length > 0 && (
                        <>
                            <Button
                                size="small"
                                startIcon={<PlayArrow />}
                                onClick={() => handleToggleAll(true)}
                                disabled={loadingSalvar || todosAbertos}
                                variant="outlined"
                                color="success"
                            >
                                Abrir todos
                            </Button>
                            <Button
                                size="small"
                                startIcon={<Stop />}
                                onClick={() => handleToggleAll(false)}
                                disabled={loadingSalvar || todosFechados}
                                variant="outlined"
                                color="error"
                            >
                                Fechar todos
                            </Button>
                        </>
                    )}
                    <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        disabled={loadingSalvar}
                        variant="outlined"
                    >
                        Atualizar
                    </Button>
                </Box>
            </CabecalhoCartao>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: 2 }}>
                {marketplacesExibir.map(marketplace => {
                    const isIfood = marketplace.tipo === 'IFOOD';
                    const ifoodStatusData = isIfood ? (ifoodStatuses[marketplace.uuid!] ?? []) : [];
                    const isOpen = isIfood
                        ? (ifoodStatusData.length > 0 ? ifoodStatusData.every(s => s.available) : false)
                        : (marketplaceStates[marketplace.uuid!] ?? false);
                    const ifoodTooltip = ifoodStatusData.length > 0
                        ? Array.from(new Set(ifoodStatusData.map(s => s.message.title))).join(' · ')
                        : 'Status gerenciado pelo iFood';
                    const cor = TipoMarketplaceCor[marketplace.tipo as keyof typeof TipoMarketplaceCor] || "#666";

                    return (
                        <Box
                            key={marketplace.uuid}
                            sx={theme => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                padding: theme.spacing(1.25, 1.75),
                                borderRadius: "10px",
                                border: `1px solid ${isOpen ? alpha(cor, 0.4) : theme.palette.divider}`,
                                backgroundColor: isOpen ? alpha(cor, 0.06) : "transparent",
                                transition: theme.transitions.create(["background-color", "border-color"], { duration: 160 }),
                                "&:hover": {
                                    backgroundColor: isOpen
                                        ? alpha(cor, 0.11)
                                        : alpha(theme.palette.text.primary, 0.03),
                                },
                            })}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                <Box sx={{
                                    width: 34,
                                    height: 34,
                                    flexShrink: 0,
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.8125rem",
                                    fontWeight: 700,
                                    color: cor,
                                    backgroundColor: alpha(cor, 0.14),
                                }}>
                                    {marketplace.tipo?.charAt(0) || '?'}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                                        {TipoMarketplaceTitulo[marketplace.tipo as keyof typeof TipoMarketplaceTitulo] || marketplace.tipo}
                                    </Typography>
                                    {marketplace?.configuracao?.nomePublico && (
                                        <Typography noWrap sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                            {marketplace.configuracao.nomePublico}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                                <PilulaStatus
                                    cor={isOpen ? theme.palette.success.main : theme.palette.error.main}
                                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                                >
                                    {isOpen ? "Aberto" : "Fechado"}
                                </PilulaStatus>
                                {isIfood ? (
                                    <Tooltip title={ifoodTooltip}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Switch checked={isOpen} disabled color="primary" />
                                            <Lock fontSize="small" sx={{ color: "text.disabled" }} />
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    <Switch
                                        checked={isOpen}
                                        onChange={(e) => handleMarketplaceToggle(marketplace, e.target.checked)}
                                        disabled={loadingSalvar}
                                        color="primary"
                                    />
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box
                sx={theme => ({
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    padding: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: alpha(
                        caixaAberto ? theme.palette.info.main : theme.palette.warning.main,
                        0.06
                    ),
                    "& > svg": {
                        fontSize: 19,
                        marginTop: "1px",
                        color: caixaAberto ? theme.palette.info.main : theme.palette.warning.main,
                    },
                })}
            >
                {caixaAberto ? <InfoOutlined /> : <WarningAmberOutlined />}
                <Box>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                        {caixaAberto ? "Caixa aberto" : "Caixa fechado"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                        {caixaAberto
                            ? "Você pode controlar o status dos marketplaces individualmente. Marketplaces abertos receberão novos pedidos."
                            : "Recomenda-se fechar todos os marketplaces quando o caixa estiver fechado para evitar pedidos não atendidos."
                        }
                    </Typography>
                </Box>
            </Box>
        </Cartao>
    );
};
